import * as React from "react";
import { Outlet, useLocation } from "react-router-dom";

import { CommandPalette, useCommandPalette } from "@/components/command/CommandPalette";
import { OdinoRail } from "@/components/odino/OdinoRail";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { EventStreamProvider } from "@/lib/eventStream";
import { usePreferencesSync } from "@/lib/usePreferencesSync";

import { CapModal } from "@/components/billing/CapModal";
import { OdinoLauncher } from "@/components/odino/OdinoLauncher";
import { SetupWizard } from "@/components/setup/SetupWizard";
import { cn } from "@/lib/cn";
import { EntitlementsProvider } from "@/lib/entitlements";
import { isFocusRoute } from "@/lib/focusMode";

import { LicenseGate } from "@/components/license/LicenseGate";
import { SupportSessionBanner } from "@/components/support/SupportSessionBanner";
import { Sidebar, SidebarNav } from "./Sidebar";
import { Topbar } from "./Topbar";

const COLLAPSED_KEY = "tray:sidebar:collapsed";
const ODINO_RAIL_KEY = "tray:odino:rail";

/** Read the persisted desktop-collapsed flag (default: false). */
function readCollapsed(): boolean {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(COLLAPSED_KEY) === "1";
}

export function AppShell() {
  // Apply persisted language + theme from /me.preferences whenever a
  // page in the authenticated shell mounts (i.e. on every login).
  usePreferencesSync();

  // Sidebar state is owned here (not inside Sidebar) so the Topbar's
  // hamburger can toggle it. One affordance, two behaviours:
  //   - mobile (<md): the hamburger opens the off-canvas Sheet drawer
  //   - desktop (>=md): the hamburger toggles the collapsed rail
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState<boolean>(readCollapsed);
  // Odino rail (ADR-0024 S2.2): state lives here so the page content can be
  // shrunk by the rail's width — the rail sits BESIDE the builder, not over it.
  const [odinoOpen, setOdinoOpen] = React.useState<boolean>(
    () => typeof window !== "undefined" && window.localStorage.getItem(ODINO_RAIL_KEY) === "1",
  );
  // Odino's in-flight state (slice D): while a live build runs we make the
  // builder `inert` so the operator can't edit underneath Odino's writes (which
  // would race the auto-refresh) — they watch, they don't co-edit.
  const [odinoBusy, setOdinoBusy] = React.useState(false);
  const mainRef = React.useRef<HTMLElement>(null);
  const location = useLocation();
  // The Odino rail is GLOBAL (available on every admin page). The `inert`
  // edit-LOCK only makes sense where Odino WRITES structure the operator could
  // be co-editing: the apps builder, and (draft-first flow builds) an open
  // workflow/ETL editor — not on a generic admin page.
  const inApps = location.pathname.startsWith("/apps");
  const inFlowEditor = /^\/(workflows|automations|etl)\/[^/]+/.test(location.pathname);
  const builderLocked = (inApps || inFlowEditor) && odinoOpen && odinoBusy;

  // Focus mode (owner UX): on the immersive editor routes the chrome
  // (sidebar + topbar) is blurred + `inert`, so a stray click can't navigate
  // away from unsaved canvas work. There is NO unlock gesture on the chrome —
  // the way out is the editor's own back button; leaving the route releases
  // the lock. NB the blur is a plain `filter` on the bars' own content (safe);
  // a `backdrop-filter` over the ReactFlow canvas would flicker (see the
  // frontend CLAUDE.md gotcha) — don't convert it.
  const focusMode = isFocusRoute(location.pathname);
  const sideChromeRef = React.useRef<HTMLDivElement>(null);
  const topChromeRef = React.useRef<HTMLDivElement>(null);

  // Toggle the real DOM `inert` imperatively (a ref, not a JSX prop, for
  // React-18 compatibility): inert disables ALL pointer + keyboard + focus
  // interaction on the subtree — a hard lock, not a CSS veneer. Same pattern
  // for the Odino build-lock on <main> and the focus-mode lock on the chrome.
  React.useEffect(() => {
    if (mainRef.current) mainRef.current.inert = builderLocked;
  }, [builderLocked]);
  React.useEffect(() => {
    if (sideChromeRef.current) sideChromeRef.current.inert = focusMode;
    if (topChromeRef.current) topChromeRef.current.inert = focusMode;
  }, [focusMode]);

  // Focus mode also COLLAPSES the sidebar (owner UX: less chrome, more
  // canvas). The pre-focus state is snapshotted in a ref and restored on
  // exit — expanded comes back expanded, collapsed stays collapsed. The
  // snapshot is taken inside the functional set so the effect needs no
  // `collapsed` dep (it must fire only on the focus TRANSITION).
  const preFocusCollapsedRef = React.useRef<boolean | null>(null);
  React.useEffect(() => {
    if (focusMode) {
      setCollapsed((c) => {
        // Snapshot ONLY once per focus session: a re-run of this effect (dev
        // StrictMode double-effect) would otherwise re-capture the already
        // FORCED collapsed=true and the exit restore would be a no-op.
        if (preFocusCollapsedRef.current === null) preFocusCollapsedRef.current = c;
        return true;
      });
    } else if (preFocusCollapsedRef.current !== null) {
      const prev = preFocusCollapsedRef.current;
      preFocusCollapsedRef.current = null;
      setCollapsed(prev);
    }
  }, [focusMode]);

  // Global Cmd+K palette — the hook owns the keyboard shortcut so it
  // works from any page without each page wiring it up.
  const palette = useCommandPalette();

  // Don't persist while focus mode holds the sidebar collapsed — that value is
  // FORCED, not chosen; persisting it would make a reload inside an editor
  // permanently eat the operator's expanded preference. (Manual toggles can't
  // happen during focus anyway: the hamburger is inert.)
  React.useEffect(() => {
    if (!focusMode) window.localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0");
  }, [collapsed, focusMode]);

  React.useEffect(() => {
    window.localStorage.setItem(ODINO_RAIL_KEY, odinoOpen ? "1" : "0");
  }, [odinoOpen]);

  // Auto-close the mobile drawer when the route changes.
  React.useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  /**
   * Single hamburger handler. We branch on viewport instead of
   * rendering two buttons because the user thinks of "menu" as one
   * affordance — what it does is an implementation detail of the
   * screen size. matchMedia mirrors the Tailwind `md` breakpoint.
   */
  function onMenuClick() {
    if (window.matchMedia("(min-width: 768px)").matches) {
      setCollapsed((c) => !c);
    } else {
      setMobileOpen(true);
    }
  }

  return (
    <EntitlementsProvider>
      {/* The on-prem licence WALL (ADR-0073 hardening). Wraps the WHOLE authenticated
      shell — an unlicensed install must present one screen, not a navigable console
      that 402s feature by feature. Pass-through on every non-enforcing build. */}
      <LicenseGate>
        <EventStreamProvider>
          {/* Global plan-cap/upsell modal (ADR-0041) — opens on any 402 from the API client. */}
          <CapModal />
          <div className="flex h-full flex-col">
            {/* "You are a visitor" bar (ADR-0081 S3). Above everything, deliberately: a
          support session that looks like the customer's own is the failure mode the
          retired impersonation feature had. It also RENEWS the visit's short token.
          Renders nothing for a normal operator. */}
            <SupportSessionBanner />
            {/* First-run setup wizard (ADR-0073 dec. 4): a full-width banner + guided
          dialog while the initial admin still holds the default password. Renders
          nothing once setup is done (derived server-side, self-latching). */}
            <SetupWizard />
            <div className="flex min-h-0 flex-1">
              {/* Focus-mode chrome wrappers: the blur/opacity ride on a WRAPPER so
          Sidebar/Topbar stay untouched; `inert` is set imperatively above. */}
              {/* `flex` is load-bearing: the aside inside relies on the flex-row
          stretch for full height (its admin band is `mt-auto`); a plain block
          wrapper would collapse it to content height. */}
              <div
                ref={sideChromeRef}
                className={cn(
                  "flex shrink-0 transition-[filter,opacity] duration-300",
                  focusMode && "pointer-events-none select-none opacity-40 blur-[2px]",
                )}
              >
                <Sidebar collapsed={collapsed} />
              </div>

              {/* Mobile drawer — slides in from the left on <md. */}
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetContent side="left" width="w-sidebar" className="md:hidden p-0">
                  <SidebarNav onNavigate={() => setMobileOpen(false)} />
                </SheetContent>
              </Sheet>

              {/* Main panel — Linear-style "lifted page".
          - lives on --surface (one step brighter than the sidebar void)
          - rounded top-left corner gives the "appoggio" cue
          - hairline border-l + border-t draws the seam against the void
          - tiny shadow sells the elevation in light mode
          - on mobile (no sidebar visible) we skip the corner/border
            so it fills the screen edge-to-edge
          `min-w-0` is critical: without it, children with long un-breakable
          content (URLs, hashes, monospace tokens) force the flex column to
          grow beyond the viewport on small screens. */}
              <div
                className={`relative flex flex-1 flex-col overflow-hidden min-w-0 bg-surface transition-[margin] duration-200 md:mt-2 md:ml-0 md:rounded-tl-xl md:border-l md:border-t md:border-border md:shadow-sm ${
                  odinoOpen ? "md:mr-[440px]" : ""
                }`}
              >
                <div
                  ref={topChromeRef}
                  className={cn(
                    "shrink-0 transition-[filter,opacity] duration-300",
                    focusMode && "pointer-events-none select-none opacity-40 blur-[2px]",
                  )}
                >
                  <Topbar
                    onMenuClick={onMenuClick}
                    sidebarCollapsed={collapsed}
                    onOpenSearch={() => palette.setOpen(true)}
                    onToggleOdino={() => setOdinoOpen((o) => !o)}
                    odinoOpen={odinoOpen}
                    hideOdinoLauncher={focusMode}
                  />
                </div>
                {/* Focus-mode Odino escape hatch: the topbar (and its launcher) is
                inert/blurred, but Odino is exactly the assistant you want while
                editing a workflow/ETL/app — so render a crisp, interactive twin
                OUTSIDE the locked chrome, pinned to the header's right edge. */}
                {focusMode && (
                  <div className="absolute right-3 top-0 z-40 flex h-topbar items-center sm:right-5">
                    <OdinoLauncher
                      open={odinoOpen}
                      onToggle={() => setOdinoOpen((o) => !o)}
                      solid
                    />
                  </div>
                )}
                <main ref={mainRef} className="relative flex-1 overflow-hidden">
                  <Outlet />
                  {/* Live-build lock overlay (slice D): a dim veil + badge over the
              builder while Odino works, so it's visibly paused (the <main> is
              also `inert`, so this is purely a visual cue). */}
                  {builderLocked && (
                    <div className="pointer-events-none absolute inset-0 z-30 flex items-start justify-center bg-background/40 backdrop-blur-[1px]">
                      <span className="mt-6 inline-flex items-center gap-2 rounded-full border border-accent/40 bg-surface/95 px-4 py-1.5 text-sm font-medium text-foreground shadow-lg">
                        <span className="h-2 w-2 animate-pulse rounded-full bg-accent" />
                        Odino sta costruendo… editor in pausa
                      </span>
                    </div>
                  )}
                </main>
              </div>

              <CommandPalette open={palette.open} onOpenChange={palette.setOpen} />

              {/* Odino companion — an embedded assistant docked as a right rail,
          available on EVERY admin page (ADR-0024 S2.2): the launcher button shows
          everywhere, not just the apps builder. The main panel above reserves
          matching space (mr) when it's open so the rail sits BESIDE the content,
          not over it. (The `inert` builder edit-lock stays apps-scoped — it only
          makes sense where Odino writes app structure.) */}
              <OdinoRail open={odinoOpen} onOpenChange={setOdinoOpen} onBusyChange={setOdinoBusy} />
            </div>
          </div>
        </EventStreamProvider>
      </LicenseGate>
    </EntitlementsProvider>
  );
}
